import api from './api.js'
import type { Race, Team, CourseLeg, LegWaypoint } from '../types/index.js'

// ─── Races ────────────────────────────────────────────────────────────────────

export const orgService = {
  getRaces(): Promise<Race[]> {
    return api.get('/org/races').then(r => r.data)
  },

  getRace(id: number): Promise<Race> {
    return api.get(`/org/races/${id}`).then(r => r.data)
  },

  createRace(data: {
    name: string; slug?: string; eventDate?: string; timezone?: string
    parentRaceId?: number; courseStartLeg?: number; courseEndLeg?: number
    allowedTeamSizes?: number[]; maxTeamSize?: number
  }): Promise<Race> {
    return api.post('/org/races', data).then(r => r.data)
  },

  deleteRace(id: number): Promise<{ ok: boolean }> {
    return api.delete(`/org/races/${id}`).then(r => r.data)
  },

  updateRace(id: number, data: Partial<{
    name: string; slug: string; eventDate: string; status: Race['status']; timezone: string
    parentRaceId: number | null; courseStartLeg: number | null; courseEndLeg: number | null
    allowedTeamSizes: number[]; maxTeamSize: number
  }>): Promise<Race> {
    return api.put(`/org/races/${id}`, data).then(r => r.data)
  },

  // ─── Teams ──────────────────────────────────────────────────────────────────

  getTeams(raceId: number): Promise<Team[]> {
    return api.get(`/org/races/${raceId}/teams`).then(r => r.data)
  },

  createTeam(raceId: number, data: {
    name: string; bibNumber?: number; division?: string; category?: string
    teamSize?: number; runnerCountActual?: number; startTime?: string | null
    coachFirstName?: string; coachLastName?: string; coachPhone?: string; coachEmail?: string
  }): Promise<Team> {
    return api.post(`/org/races/${raceId}/teams`, data).then(r => r.data)
  },

  updateTeam(teamId: number, data: {
    name?: string; bibNumber?: number | null; division?: string | null; category?: string | null
    teamSize?: number; runnerCountActual?: number | null; startTime?: string | null
    coachFirstName?: string | null; coachLastName?: string | null
    coachPhone?: string | null; coachEmail?: string | null
  }): Promise<Team> {
    return api.put(`/org/teams/${teamId}`, data).then(r => r.data)
  },

  deleteTeam(teamId: number): Promise<void> {
    return api.delete(`/org/teams/${teamId}`).then(r => r.data)
  },

  getTeamRunners(teamId: number): Promise<Array<{
    id: number; name: string; vanNumber: number; runnerNumber: number
    phoneNumber: string | null; status: string
  }>> {
    return api.get(`/org/teams/${teamId}/runners`).then(r => r.data)
  },

  getTeamLegs(teamId: number): Promise<Array<{
    id: number; legNumber: number; runnerId: number | null; runnerName: string | null; runnerVan: number | null
  }>> {
    return api.get(`/org/teams/${teamId}/legs`).then(r => r.data)
  },

  getTeamByJoinCode(code: string): Promise<Team> {
    return api.get(`/org/teams/join/${code}`).then(r => r.data)
  },

  getTeamWatchData(slug: string, bib: number): Promise<{
    team: Team & { raceName: string; raceStatus: string }
    raceState: { currentLegNumber: number; currentRunnerId: number | null; raceStartTime: string | null; legStartTime: string | null; raceStatus: string } | null
    runners: Array<{ id: number; name: string; vanNumber: number; runnerNumber: number; status: string }>
    legs: Array<any>
    logs: Array<any>
    locations: Array<{ runnerId: number; latitude: number; longitude: number; accuracy: number | null; timestamp: string }>
  }> {
    return api.get(`/org/races/${slug}/watch/${bib}`).then(r => r.data)
  },

  joinTeam(joinCode: string): Promise<{ teamId: number; teamName: string; raceId: number; joinCode: string }> {
    return api.post('/org/teams/join', { joinCode }).then(r => r.data)
  },

  // ─── Course legs ─────────────────────────────────────────────────────────────

  getCourseLegs(raceId: number): Promise<CourseLeg[]> {
    return api.get(`/org/races/${raceId}/course-legs`).then(r => r.data)
  },

  updateCourseLeg(id: number, data: Partial<CourseLeg>): Promise<CourseLeg> {
    return api.put(`/org/course-legs/${id}`, data).then(r => r.data)
  },

  createCourseLeg(raceId: number, data: { afterLegNumber: number; startLat: number; startLng: number; endLat: number; endLng: number }): Promise<CourseLeg> {
    return api.post(`/org/races/${raceId}/course-legs`, data).then(r => r.data)
  },

  deleteCourseLeg(id: number): Promise<{ ok: boolean; deletedLegNumber: number }> {
    return api.delete(`/org/course-legs/${id}`).then(r => r.data)
  },

  getWaypoints(courseLegId: number): Promise<LegWaypoint[]> {
    return api.get(`/org/course-legs/${courseLegId}/waypoints`).then(r => r.data)
  },

  replaceWaypoints(courseLegId: number, waypoints: Array<{ latitude: number; longitude: number; elevationM?: number }>): Promise<{ count: number }> {
    return api.put(`/org/course-legs/${courseLegId}/waypoints`, { waypoints }).then(r => r.data)
  },

  // ─── Van route ───────────────────────────────────────────────────────────────

  getVanRoute(courseLegId: number): Promise<Array<{ latitude: number; longitude: number }>> {
    return api.get(`/org/course-legs/${courseLegId}/van-route`).then(r => r.data)
  },

  replaceVanRoute(courseLegId: number, points: Array<{ latitude: number; longitude: number }>): Promise<{ count: number }> {
    return api.put(`/org/course-legs/${courseLegId}/van-route`, { points }).then(r => r.data)
  },

  // ─── Broadcast ───────────────────────────────────────────────────────────────

  getSmsCoverage(raceId: number): Promise<any[]> {
    return api.get(`/org/races/${raceId}/sms-coverage`).then(r => r.data)
  },

  getRaceLog(raceId: number): Promise<any[]> {
    return api.get(`/org/races/${raceId}/race-log`).then(r => r.data)
  },

  getVanLocations(raceId: number): Promise<any[]> {
    return api.get(`/org/races/${raceId}/van-locations`).then(r => r.data)
  },

  recordFinishTime(teamId: number, finishTime: string | null): Promise<any> {
    return api.put(`/org/teams/${teamId}/finish-time`, { finishTime }).then(r => r.data)
  },

  sendBroadcast(raceId: number, message: string, isEmergency = false): Promise<{ sent: number; failed: number; total: number; pushSent?: number }> {
    return api.post(`/org/races/${raceId}/broadcast`, { message, isEmergency }).then(r => r.data)
  },

  getBroadcastHistory(raceId: number): Promise<Array<{ id: number; message: string; sent_count: number; failed_count: number; total_count: number; sent_at: string }>> {
    return api.get(`/org/races/${raceId}/broadcast-history`).then(r => r.data)
  },

  // ─── Multi-team locations ─────────────────────────────────────────────────────

  getAllTeamsLocations(raceId: number): Promise<any[]> {
    return api.get(`/org/races/${raceId}/locations`).then(r => r.data)
  },

  // ─── Organizer registration ───────────────────────────────────────────────────

  lookupRegistrationCode(code: string): Promise<{ id: number; name: string; eventDate: string | null; slug: string | null }> {
    return api.get(`/org/register/race/${code}`).then(r => r.data)
  },

  registerOrganizer(data: { code: string; firstName?: string; lastName?: string; email: string; password: string }): Promise<{ success: boolean; message: string }> {
    return api.post('/org/register/race', data).then(r => r.data)
  },

  // ─── Race organizer assignments (system_admin only) ──────────────────────────

  getRaceOrganizers(raceId: number): Promise<Array<{ id: number; username: string; firstName: string | null; lastName: string | null; email: string | null; assignedAt: string }>> {
    return api.get(`/org/races/${raceId}/organizers`).then(r => r.data)
  },

  addRaceOrganizer(raceId: number, userId: number): Promise<{ success: boolean }> {
    return api.post(`/org/races/${raceId}/organizers`, { userId }).then(r => r.data)
  },

  removeRaceOrganizer(raceId: number, userId: number): Promise<{ success: boolean }> {
    return api.delete(`/org/races/${raceId}/organizers/${userId}`).then(r => r.data)
  },

  // ─── User management (system_admin only) ─────────────────────────────────────

  getUsers(): Promise<any[]> {
    return api.get('/org/users').then(r => r.data)
  },

  updateUserRole(userId: number, role: string): Promise<void> {
    return api.put(`/org/users/${userId}/role`, { role }).then(r => r.data)
  },

  deleteUser(userId: number): Promise<void> {
    return api.delete(`/org/users/${userId}`).then(r => r.data)
  },
}
